import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
    X,
    User,
    Lock,
    Phone,
    Building2,
    Briefcase,
    DollarSign,
    CheckCircle,
    Eye,
    EyeOff,
    KeyRound,
    UserCheck2,
    Upload,
    Loader2,
} from "@/components/icons/index";

import { ValidateInput } from "@/components/SearchBox/ValidateInput";
import { SearchBar } from "@/components/SearchBox/SearchBar";
import { validateEmail, validateUsername } from "@/utils/validators";
import { showToast } from "@/redux/Toast/actions";
import useAuthUser from "@/hooks/useAuthUser";
import { useTranslation } from "react-i18next";
import { createCustomer, updateCustomer } from "@/redux/Masters/customers/actions";

/* ─── Style tokens ───────────────────────────────────────────── */
const inputBase =
    "w-full px-3 py-2 bg-white dark:bg-slate-800 border rounded-lg text-sm " +
    "text-slate-900 dark:text-white placeholder-slate-400 " +
    "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all";
const labelBase =
    "block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1";
const selectBase =
    "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 " +
    "rounded-lg text-sm text-slate-900 dark:text-white appearance-none cursor-pointer " +
    "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all";
const errorBase = "mt-1 text-[11px] text-red-500 font-medium";

/* ─── Required fields ────────────────────────────────────────── */
const REQUIRED_FIELDS = [

    { key: "PROFILE_FIRSTNAME", labelKey: "customersPage.firstName" },
    { key: "PROFILE_EMAILID", labelKey: "customersPage.emailAddress" },
    { key: "COMPANYNAME", labelKey: "customersPage.companyName" },
    { key: "ROLEID", labelKey: "customersPage.role" },
    { key: "PROFILE_USERNAME", labelKey: "customersPage.username" },
];
const PASSWORD_FIELD = { key: "PROFILE_PASSWORD", labelKey: "customersPage.password" };

/* ─── Field wrapper ──────────────────────────────────────────── */
function Field({ label, required, error, children }) {
    return (
        <div>
            <label className={labelBase}>
                {label}
                {required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            {children}
            {error && <p className={errorBase}>{error}</p>}
        </div>
    );
}

/* ─── Section divider ────────────────────────────────────────── */
function SectionDivider({ icon: Icon, title, color = "blue" }) {
    const colors = {
        blue: "bg-blue-500",
        green: "bg-emerald-500",
        purple: "bg-purple-500",
        amber: "bg-amber-500",
    };
    return (
        <div className="flex items-center gap-2 py-1">
            <div className={`p-1.5 rounded-lg ${colors[color]} shadow-sm shrink-0`}>
                <Icon className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 whitespace-nowrap">
                {title}
            </span>
            <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
        </div>
    );
}

/* ─── Image Upload Field ─────────────────────────────────────── */
function ImageUploadField({
    value,
    onChange,
    label = "Profile Image",
    accept = "image/png,image/jpeg,image/webp",
    hint = "PNG, JPG, WEBP · Max 5 MB",
}) {
    const fileRef = useRef(null);

    /* ── Read file → base-64 data-URL ── */
    const handleFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => onChange(reader.result, file);
        reader.readAsDataURL(file);
    };

    /* ── Clear selection ── */
    const handleRemove = () => {
        onChange(null, null);
        if (fileRef.current) fileRef.current.value = "";
    };

    return (
        <div>
            <label className={labelBase}>{label}</label>

            {value ? (
                /* ── Filled state ──────────────────────────────────────────── */
                <div
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg
                                border border-slate-200 dark:border-slate-700
                                bg-slate-50 dark:bg-slate-800"
                >
                    <img
                        src={value}
                        alt="Logo preview"
                        className="w-11 h-11 rounded-lg object-cover shrink-0
                                   border border-slate-200 dark:border-slate-600"
                        onError={(e) => {
                            e.target.style.display = "none";
                        }}
                    />

                    <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-semibold text-slate-700 dark:text-slate-200">
                            Image selected
                        </p>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                            Click "Change" to replace or "Remove" to clear
                        </p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        {/* Change */}
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            className="h-8 px-3 rounded-lg text-[12px] font-semibold
                                       bg-white dark:bg-slate-700
                                       border border-slate-300 dark:border-slate-600
                                       text-slate-700 dark:text-slate-200
                                       hover:bg-slate-100 transition-colors"
                        >
                            Change
                        </button>

                        {/* Remove */}
                        <button
                            type="button"
                            onClick={handleRemove}
                            className="h-8 px-3 rounded-lg text-[12px] font-semibold
                                       inline-flex items-center gap-1
                                       bg-red-50 dark:bg-red-900/20
                                       border border-red-200 dark:border-red-800
                                       text-red-600 dark:text-red-400
                                       hover:bg-red-100 transition-colors"
                        >
                            <X className="w-3 h-3" /> Remove
                        </button>
                    </div>
                </div>
            ) : (
                /* ── Empty / upload state ──────────────────────────────────── */
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                               border border-dashed border-slate-300 dark:border-slate-600
                               bg-slate-50 dark:bg-slate-800/50
                               hover:border-blue-400 hover:bg-blue-50/30
                               transition-all group"
                >
                    <div
                        className="w-9 h-9 rounded-lg shrink-0
                                    bg-slate-200 dark:bg-slate-700
                                    group-hover:bg-blue-100
                                    flex items-center justify-center transition-colors"
                    >
                        <Upload className="w-4 h-4 text-slate-400 group-hover:text-blue-500 transition-colors" />
                    </div>
                    <div className="text-left">
                        <p
                            className="text-[12px] font-semibold
                                      text-slate-600 dark:text-slate-300
                                      group-hover:text-blue-600 transition-colors"
                        >
                            Upload image
                        </p>
                        <p className="text-[11px] text-slate-400">{hint}</p>
                    </div>
                </button>
            )}

            {/* Hidden file input */}
            <input
                ref={fileRef}
                type="file"
                accept={accept}
                className="hidden"
                onChange={handleFile}
            />
        </div>
    );
}

/* ══════════════════════════════════════════════════════════════
   Main Modal
══════════════════════════════════════════════════════════════ */
const CustomerModal = ({
    open,
    onClose,
    formData,
    errors,
    handleInputChange,
    onSave,
    roleList = [],
}) => {
    console.log('roleList: ', roleList);
    console.log('formData modal: ', formData);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [showPassword, setShowPassword] = useState(false);
    const [profileImage, setProfileImage] = useState(null);
    const [profileImageFile, setProfileImageFile] = useState(null);
    const [logoLight, setLogoLight] = useState(null);
    const [logoDark, setLogoDark] = useState(null);
    const [logoMobile, setLogoMobile] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const authUser = useAuthUser();
    const isEdit = Number(formData?.CUST_ID) > 0;

    // Reset local state whenever modal opens — pre-fills previews for edit mode
    // from whatever URL strings the API returned on formData.
    useEffect(() => {
        if (open) {
            setShowPassword(false);
            setLogoLight(
                typeof formData?.CUSTCOMPANYLOGOLIGHT === "string"
                    ? formData.CUSTCOMPANYLOGOLIGHT
                    : null,
            );
            setLogoDark(
                typeof formData?.CUSTCOMPANYLOGODARK === "string"
                    ? formData.CUSTCOMPANYLOGODARK
                    : null,
            );
            setLogoMobile(
                typeof formData?.CUSTCOMPANYLOGOMOBILE === "string"
                    ? formData.CUSTCOMPANYLOGOMOBILE
                    : null,
            );
        }
    }, [open]);

    /* ─── Validate required fields ──────────────────────────── */
    const validateFields = () => {
        const fields = [...REQUIRED_FIELDS];
        if (!isEdit) fields.push(PASSWORD_FIELD);

        const missing = fields.filter((f) => {
            const val = formData?.[f.key];
            return (
                !val || String(val).trim() === "" || val === "0" || Number(val) === 0
            );
        });

        if (missing.length) {
            dispatch(
                showToast(
                    `${t('requiredFields')}: ${missing.map((f) => t(f.labelKey)).join(", ")}`,
                    "error",
                ),
            );
            return false;
        }

        // Password length validation
        const password = String(formData?.PROFILE_PASSWORD || "");

        if (password && password.length < 8) {
            dispatch(
                showToast(
                    t("customersPage.minEightChars"),
                    "error"
                )
            );
            return false;
        }
        return true;
    };

    /* ─── Save handler ──────────────────────────────────────── */
    const handleSave = async () => {
        if (!validateFields()) return;

        setIsSaving(true);

        try {
            const createdBy = authUser?.username || "";

            const fd = new FormData();

            fd.append("PROFILE_EMAILID", formData?.PROFILE_EMAILID || "");

            fd.append("PROFILE_FIRSTNAME", formData?.PROFILE_FIRSTNAME || "");

            fd.append("PROFILE_LASTNAME", formData?.PROFILE_LASTNAME || "");

            fd.append("PROFILE_USERNAME", formData?.PROFILE_USERNAME || "");

            fd.append("PROFILE_PASSWORD", formData?.PROFILE_PASSWORD || "");

            fd.append("COMPANYNAME", formData?.COMPANYNAME || "");

            fd.append("CUST_ID", Number(formData?.CUST_ID) || 0);

            fd.append(
                "COMPANY_ID",
                Number(authUser?.companyId || 0)
            );

            fd.append(
                "ADB_COMPANY_ID",
                Number(formData?.ADB_COMPANY_ID) || 0
            );

            fd.append("ROLEID", Number(formData?.ROLEID) || 0);

            fd.append("CREATED_BY", createdBy);

            fd.append("ISACTIVE", formData?.ISACTIVE ?? true);

            // Company logos — send the File if the user picked a new one,
            // otherwise keep whatever URL string is already on formData
            // (edit mode) so the backend doesn't clear the existing logo.
            const appendLogo = (field) => {
                const val = formData?.[field];
                if (val instanceof File) {
                    fd.append(field, val, val.name);
                } else if (typeof val === "string" && val) {
                    fd.append(field, val);
                } else {
                    fd.append(field, "");
                }
            };
            appendLogo("CUSTCOMPANYLOGOLIGHT");
            appendLogo("CUSTCOMPANYLOGODARK");
            appendLogo("CUSTCOMPANYLOGOMOBILE");

            if (isEdit) {
                await dispatch(updateCustomer(fd));

                dispatch(
                    showToast(
                        t("customerProfile.customerUpdated"),
                        "success",
                        true
                    )
                );
            } else {
                await dispatch(createCustomer(fd));

                dispatch(
                    showToast(
                        t("customerProfile.customerCreated"),
                        "success",
                        true
                    )
                );
            }

            onSave?.();
            onClose();
        } catch (err) {
            console.error("Save customer error:", err);

            dispatch(
                showToast(
                    t("customerProfile.saveFailed"),
                    "error"
                )
            );
        } finally {
            setIsSaving(false);
        }
    };

    if (!open) return null;

    // ValidateInput class — matches the rest of the form inputs
    const validateInputClass =
        "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 " +
        "text-slate-900 dark:text-white focus:ring-blue-500";

    return (
        <AnimatePresence>
            {open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={onClose}
                    />

                    {/* Panel */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 16 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 16 }}
                        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                        className="relative z-10 w-full max-w-3xl max-h-[92vh] flex flex-col bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* ── Header ── */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 bg-gradient-to-r from-slate-600 to-slate-700 shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-md">
                                    <User className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-base font-bold text-white">
                                        {isEdit ? t('customersPage.editCustomer') : t('customersPage.createNewCustomer')}
                                    </h2>
                                    <p className="text-[11px] text-slate-400">
                                        {isEdit
                                            ? t('customersPage.editingCustomer', { name: `${formData?.PROFILE_FIRSTNAME || ""} ${formData?.PROFILE_LASTNAME || ""}`.trim() })
                                            : t('customersPage.fillRequiredToCreate')}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-600 transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* ── Body ── */}
                        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                            {/* IDENTITY */}
                            <SectionDivider icon={User} title={t('customersPage.identity')} color="blue" />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field label={t('customersPage.firstName')} required error={errors?.PROFILE_FIRSTNAME}>
                                    <input
                                        name="PROFILE_FIRSTNAME"
                                        value={formData?.PROFILE_FIRSTNAME || ""}
                                        onChange={handleInputChange}
                                        placeholder={t('customersPage.enterFirstName')}
                                        autoComplete="off"
                                        className={`${inputBase} ${errors?.PROFILE_FIRSTNAME ? "border-red-400 focus:ring-red-400" : "border-slate-200 dark:border-slate-700"}`}
                                    />
                                </Field>

                                <Field label={t('customersPage.lastName')} error={errors?.PROFILE_LASTNAME}>
                                    <input
                                        name="PROFILE_LASTNAME"
                                        value={formData?.PROFILE_LASTNAME || ""}
                                        onChange={handleInputChange}
                                        placeholder={t('customersPage.enterLastName')}
                                        autoComplete="off"
                                        className={`${inputBase} ${errors?.PROFILE_LASTNAME ? "border-red-400 focus:ring-red-400" : "border-slate-200 dark:border-slate-700"}`}
                                    />
                                </Field>

                                <div>
                                    <label className={labelBase}>
                                        {t('customersPage.emailAddress')} <span className="text-red-500">*</span>
                                    </label>
                                    <ValidateInput
                                        type="email"
                                        value={formData?.PROFILE_EMAILID || ""}
                                        initialValue={formData?.PROFILE_EMAILID}
                                        isEdit={isEdit}
                                        onChange={(value) =>
                                            handleInputChange({
                                                target: { name: "PROFILE_EMAILID", value },
                                            })
                                        }
                                        placeholder={t('customersPage.emailPlaceholder')}
                                        minLength={5}
                                        debounceTime={700}
                                        inputClassName={validateInputClass}
                                        validator={validateEmail}
                                        autoComplete="off"
                                    />
                                    {errors?.PROFILE_EMAILID && (
                                        <p className={errorBase}>{errors.PROFILE_EMAILID}</p>
                                    )}
                                </div>

                                <Field label={t('customersPage.role')} required error={errors?.ROLEID}>
                                    <select
                                        name="ROLEID"
                                        value={formData?.ROLEID || ""}
                                        onChange={handleInputChange}
                                        className={`${selectBase} ${errors?.ROLEID ? "border-red-400 focus:ring-red-400" : ""}`}
                                    >
                                        <option value="">{t('customersPage.selectRole')}</option>
                                        {roleList?.length > 0 ? (
                                            roleList.map((r) => (
                                                <option key={r.roleId} value={r.roleId}>
                                                    {r.roleName}
                                                </option>
                                            ))
                                        ) : (
                                            <>
                                                <option value="2">Company Admin</option>
                                                <option value="3">Operation User</option>
                                                <option value="4">Manager</option>
                                                <option value="5">DATA ENTRY</option>
                                                <option value="6">Viewer</option>
                                                <option value="7">Accounts</option>
                                            </>
                                        )}
                                    </select>
                                </Field>

                                {/* Address-book company lookup — SearchBar untouched.
                                    searchAPI is the URL prefix; SearchBar appends the typed
                                    value then "&compnayId=<MasterCompanyDetails.CompanyId>".
                                    Result rows come back as Object.values(row), so with the
                                    API's key order (totalRows, rowNum, id, companyCode,
                                    customerName, ...) index 2 = id, index 4 = customerName. */}
                                <div>
                                    <label className={labelBase}>
                                        {t('companyName')} <span className="text-red-500">*</span>
                                    </label>
                                    <SearchBar
                                        value={formData?.COMPANYNAME || ""}
                                        searchAPI="v1/AddressBook/GetCompanyAddressList?IPageNo=1&IPageRecords=500&SearchText="
                                        paramCompanyId="compnayId"
                                        showResults={4}
                                        minword={1}
                                        onchange={(val) =>
                                            handleInputChange({
                                                target: { name: "COMPANYNAME", value: val },
                                            })
                                        }
                                        selectReacord={(result) => {
                                            handleInputChange({
                                                target: { name: "ADB_COMPANY_ID", value: result?.[2] },
                                            });
                                            handleInputChange({
                                                target: { name: "COMPANYNAME", value: result?.[4] },
                                            });
                                        }}
                                        inputStyle="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white"
                                    />
                                    {errors?.COMPANYNAME && (
                                        <p className={errorBase}>{errors.COMPANYNAME}</p>
                                    )}
                                </div>

                                <Field label={t("ISACTIVE")}>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            handleInputChange({
                                                target: {
                                                    name: "ISACTIVE",
                                                    value: !(formData?.ISACTIVE ?? true),
                                                },
                                            })
                                        }
                                        className={` w-full h-[38px] px-3 rounded-lg border flex items-center justify-between text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500
                                                ${formData?.ISACTIVE
                                                ? "bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300"
                                                : "bg-slate-50 border-slate-200 text-slate-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400"
                                            }
                                        `}
                                    >
                                        <span>
                                            {formData?.ISACTIVE
                                                ? t("active")
                                                : t("inactive")}
                                        </span>

                                        <span
                                            className={`
                                                w-2 h-2 rounded-full
                                                ${formData?.ISACTIVE
                                                    ? "bg-emerald-500"
                                                    : "bg-slate-400 dark:bg-slate-500"
                                                }
                                        `}
                                        />
                                    </button>
                                </Field>
                            </div>

                            {/* COMPANY BRANDING */}
                            <SectionDivider icon={Building2} title={t('customersPage.companyBranding')} color="amber" />
                            <div className="grid grid-cols-1  gap-4">
                                {/* Single shared logo for now — same file goes to all three
                                    fields (CUSTCOMPANYLOGOLIGHT/DARK/MOBILE). Separate uploads
                                    per variant are commented out below for later. */}
                                <ImageUploadField
                                    label={t('companyLogo')}
                                    value={logoLight}
                                    onChange={(dataUrl, file) => {
                                        setLogoLight(dataUrl);
                                        setLogoDark(dataUrl);
                                        setLogoMobile(dataUrl);
                                        handleInputChange({
                                            target: { name: "CUSTCOMPANYLOGOLIGHT", value: file },
                                        });
                                        handleInputChange({
                                            target: { name: "CUSTCOMPANYLOGODARK", value: file },
                                        });
                                        handleInputChange({
                                            target: { name: "CUSTCOMPANYLOGOMOBILE", value: file },
                                        });
                                    }}
                                />
                                {/*
                                <ImageUploadField
                                    label={t('customersPage.logoLight')}
                                    value={logoLight}
                                    onChange={(dataUrl, file) => {
                                        setLogoLight(dataUrl);
                                        handleInputChange({
                                            target: { name: "CUSTCOMPANYLOGOLIGHT", value: file },
                                        });
                                    }}
                                />
                                <ImageUploadField
                                    label={t('customersPage.logoDark')}
                                    value={logoDark}
                                    onChange={(dataUrl, file) => {
                                        setLogoDark(dataUrl);
                                        handleInputChange({
                                            target: { name: "CUSTCOMPANYLOGODARK", value: file },
                                        });
                                    }}
                                />
                                <ImageUploadField
                                    label={t('customersPage.logoMobile')}
                                    value={logoMobile}
                                    onChange={(dataUrl, file) => {
                                        setLogoMobile(dataUrl);
                                        handleInputChange({
                                            target: { name: "CUSTCOMPANYLOGOMOBILE", value: file },
                                        });
                                    }}
                                />
                                */}
                            </div>

                            {/* CREDENTIALS */}
                            <SectionDivider
                                icon={KeyRound}
                                title={t('customersPage.credentials')}
                                color="purple"
                            />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {/* Username — ValidateInput for live duplicate check UI only.
                                    No status gating on save button. */}
                                <div>
                                    <label className={labelBase}>
                                        {t('username')} <span className="text-red-500">*</span>
                                    </label>
                                    <ValidateInput
                                        value={formData?.PROFILE_USERNAME || ""}
                                        onChange={(value) =>
                                            handleInputChange({
                                                target: { name: "PROFILE_USERNAME", value },
                                            })
                                        }
                                        placeholder={t('customersPage.enterUsername')}
                                        minLength={3}
                                        debounceTime={700}
                                        inputClassName={validateInputClass}
                                        validator={validateUsername}
                                        autoComplete="off"
                                    />
                                    {errors?.PROFILE_USERNAME && (
                                        <p className={errorBase}>{errors.PROFILE_USERNAME}</p>
                                    )}
                                </div>

                                <Field
                                    label={t('customersPage.password')}
                                    required={!isEdit}
                                    error={errors?.PROFILE_PASSWORD}
                                >
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                                        <input
                                            name="PROFILE_PASSWORD"
                                            type={showPassword ? "text" : "password"}
                                            value={formData?.PROFILE_PASSWORD || ""}
                                            onChange={handleInputChange}
                                            placeholder={t('customersPage.minEightChars')}
                                            autoComplete="new-password"
                                            className={`${inputBase} pl-9 pr-9 ${errors?.PROFILE_PASSWORD ? "border-red-400 focus:ring-red-400" : "border-slate-200 dark:border-slate-700"}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword((v) => !v)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                        >
                                            {showPassword ? (
                                                <EyeOff className="w-3.5 h-3.5" />
                                            ) : (
                                                <Eye className="w-3.5 h-3.5" />
                                            )}
                                        </button>
                                    </div>
                                </Field>

                            </div>

                            <p className="text-[11px] text-slate-400 dark:text-slate-500">
                                <span className="text-red-500">*</span> {t('customersPage.requiredFieldsNote')}
                            </p>
                        </div>

                        {/* ── Footer ── */}
                        <div className="shrink-0 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="inline-flex items-center gap-1.5 h-9 px-4 text-sm font-medium text-red-500 hover:text-red-600 transition-colors"
                            >
                                <X className="w-3.5 h-3.5" /> {t('cancel')}
                            </button>

                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={isSaving}
                                className={`inline-flex items-center gap-2 h-9 px-5 rounded-lg text-sm font-semibold text-white transition-all
                                    ${isSaving
                                        ? "bg-slate-400 cursor-not-allowed opacity-60"
                                        : "bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-[0_4px_14px_-4px_rgba(37,99,235,0.45)]"
                                    }`}
                            >
                                {isSaving ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                    <UserCheck2 className="w-3.5 h-3.5" />
                                )}
                                {isEdit ? t('Update Customer') : t('Create Customer')}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default CustomerModal;